import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getAttachedServiceRequests } from '../../../../api'
import { EMPTY_OBJECT, NOTIFICATION_TYPE } from '../../../../constants/common'
import { useValidationWithoutFormikNotifications } from '../../../../hooks'
import {
  useTableModalCloseHandler,
  useTableModalLoadingData,
} from '../../../TableModal'
import {
  ATTACH_SERVICE_REQUESTS_NOTIFICATION_ID,
  ATTACH_SERVICE_REQUESTS_PAGINATION_PAGE_SIZE_KEY,
  AttachServiceRequestsStatusModal,
  getAttachServiceRequestsFromResponse,
  getAttachServiceRequestsInitialNotifications,
  getAttachServiceRequestsModalTableColumns,
  getAttachServiceRequestsStatusAction,
  getRelatedRequestsLimitErrorMeta,
  isEqualAttachServiceRequestModal,
} from '../../AttachServiceRequestsModal'
import { useRequestLoadExtraTableData } from '../useRequestLoadExtraTableData'

export function useAttachServiceRequestModal({
  allowEmptySelection,
  apiParams = EMPTY_OBJECT,
  canSelectOnlyOneNotificationKey,
  changeStatusAttention = false,
  entityId,
  excludeIds,
  getItemsFromResponse = getAttachServiceRequestsFromResponse,
  initialSelection,
  loadItems = getAttachedServiceRequests,
  multipleSelection,
  readonly,
  restoreAttention = true,
  showLimitNotification,
  onAttach: onAttachRequestAsync,
  onClose,
  onSelectedAttachRequests,
  ...restProps
}) {
  const { t } = useTranslation()

  const [modal, setModal] = useState(null)

  async function getItems(id, params) {
    return loadItems({
      ...apiParams,
      excludeIds,
      id,
      params,
    })
  }

  const loadData = useTableModalLoadingData({
    ...restProps,
    entityId,
    getItemsFromResponse,
    initialNotifications: getAttachServiceRequestsInitialNotifications({
      canSelectOnlyOneNotificationKey,
      multipleSelection,
      readonly,
      showLimitNotification,
      t,
    }),
    initialSelection,
    loadItems: getItems,
    paginationPageSizeKey: ATTACH_SERVICE_REQUESTS_PAGINATION_PAGE_SIZE_KEY,
  })

  const {
    handlePrependNotification,
    handleRemoveNotification,
    initialSelectionValues,
    selected,
    setLoading,
    tableItems,
  } = loadData

  const { extra } = useRequestLoadExtraTableData(tableItems?.items)

  const { handleCloseTableModal, isCloseConfirm, setCloseConfirm } =
    useTableModalCloseHandler({
      initialSelectionValues,
      selected,
      onClose,
    })

  const { isValidForm, setNotificationListRef } =
    useValidationWithoutFormikNotifications({
      validationSchemaProps: {
        isEqual: isEqualAttachServiceRequestModal,
        isEqualProps: { allowEmptySelection, selected },
      },
      onRemoveNotification: handleRemoveNotification,
      onSetNotification: handlePrependNotification,
    })

  function handleAttachRequest({ payload } = EMPTY_OBJECT) {
    if (!onAttachRequestAsync) {
      onClose()

      return
    }

    setLoading(true)

    onAttachRequestAsync({
      ...payload,
      entityId,
      id: selected[0].id,
      name: selected[0].title,
      selected,
    })
      .catch(error => {
        const limitErrorMeta = getRelatedRequestsLimitErrorMeta(error)

        if (limitErrorMeta) {
          handlePrependNotification({
            id: ATTACH_SERVICE_REQUESTS_NOTIFICATION_ID.limitExceeded,
            text: t(
              'Modal.AttachServiceRequestsLimitExceededKey',
              limitErrorMeta
            ),
            type: NOTIFICATION_TYPE.error,
          })
        }
      })
      .finally(() => setLoading(false))
  }

  function handleCloseModal() {
    setModal(null)
  }

  function handleAttach() {
    if (!isValidForm()) {
      return
    }

    if (allowEmptySelection && selected.length === 0) {
      onSelectedAttachRequests?.(selected)

      return
    }

    const statusAction = getAttachServiceRequestsStatusAction({
      changeStatusAttention,
      item: selected[0],
      restoreAttention,
    })

    if (statusAction) {
      setModal(
        <AttachServiceRequestsStatusModal
          action={statusAction}
          onClose={handleCloseModal}
          onSubmit={handleAttachRequest}
        />
      )

      return
    }

    if (onSelectedAttachRequests) {
      onSelectedAttachRequests(selected)
    } else {
      handleAttachRequest()
    }
  }

  // Confirm modal expects promise to resolve it and fire `finally` to stop loading
  function handleApplyConfirmModal() {
    return new Promise(resolve => {
      onClose()

      resolve()
    })
  }

  const tableColumns = getAttachServiceRequestsModalTableColumns(readonly)

  return {
    extra,
    handleApplyConfirmModal,
    handleAttach,
    handleCloseTableModal,
    isCloseConfirm,
    loadData,
    modal,
    setCloseConfirm,
    setNotificationListRef,
    tableColumns,
  }
}
